import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

interface ErrorBoundaryProps {
  onError: (error: Error, errorInfo: React.ErrorInfo) => void;
  children: React.ReactNode;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  { hasError: boolean }
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError(error, errorInfo);
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

function withRenderTimeLogger<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName = WrappedComponent.name || "Component"
) {
  return function RenderTimeLoggerWrapper(props: P) {
    const startTimeRef = useRef(typeof window !== 'undefined' && typeof window.performance !== 'undefined' ? window.performance.now() : Date.now());
    const location = useLocation();

    const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
      console.error(
        `[Error] ${componentName} failed to render:`,
        error,
        errorInfo
      );

      // Optional: send to a logging service
    };

    useEffect(() => {
      if (typeof window !== 'undefined' && typeof window.performance !== 'undefined') {
        const endTime = window.performance.now();
        const totalRenderTime = endTime - startTimeRef.current;
        console.log(
          `[Render Time] ${componentName}: ${totalRenderTime.toFixed(2)}ms - ${
            location.pathname
          }`
        );
      }
    }, []);

    return (
      <ErrorBoundary onError={handleError}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}
export default withRenderTimeLogger;
